import { Button, Form } from 'react-bootstrap';
import GeometryToolsPanel from '../../../GeometryToolsPanel/GeometryToolsPanel';
import OutsideAreaBadge from '../../../OutsideAreaBadge/OutsideAreaBadge';
import ShapePlacementControls from '../../../ShapePlacementControls/ShapePlacementControls';
import {
  getIpawsAreaShapeLabel,
  getIpawsAreaMetrics,
  getIpawsPolygonRequiredPointCapacity,
  IPAWS_AREA_LIMITS,
  IPAWS_AREA_TYPES,
  MINIMUM_IPAWS_SHAPE_SIZE_KM,
  NWEM_MAXIMUM_DRAWN_POLYGON_VERTEX_COUNT,
} from '../../../../../../utils/ipawsArea.utils';

/**
 * Shows the controls for placing and editing IPAWS alert areas. This component calculates what the
 * user may do, but sends every requested change to its parent so the map remains the single owner of
 * placement behavior.
 *
 * @param {Object} props
 * @param {Array<Object>} props.shapes Current polygon, square, and circle definitions.
 * @param {string|null} props.activeTool Shape type currently being placed.
 * @param {string|null} props.selectedShapeId ID of the shape being edited.
 * @param {string[]} [props.outsideShapeIds] IDs of the shapes outside the authorized area.
 * @param {boolean} props.isNwemSelected Whether NWEM-specific polygon limits apply.
 * @param {Function} props.onStartTool Starts placing the chosen shape type.
 * @param {Function} props.onFinishTool Keeps the current shape and ends placement.
 * @param {Function} props.onCancelTool Cancels the current placement.
 * @param {Function} props.onPlaceAtMapCenter Places the next point or shape at the map center.
 * @param {Function} props.onSelectShape Selects a placed shape for editing.
 * @param {Function} props.onContinuePolygon Resumes adding points to a selected polygon.
 * @param {Function} props.onUpdateSelectedShape Updates fields on the selected shape.
 * @param {Function} props.onDeleteSelectedShape Deletes the selected shape.
 * @param {Function} props.onClearShapes Deletes all placed shapes.
 * @returns {JSX.Element}
 */
const IpawsAreaToolsPanel = ({
  shapes,
  activeTool,
  selectedShapeId,
  outsideShapeIds = [],
  isNwemSelected = false,
  onStartTool,
  onFinishTool,
  onCancelTool,
  onPlaceAtMapCenter,
  onSelectShape,
  onContinuePolygon,
  onUpdateSelectedShape,
  onDeleteSelectedShape,
  onClearShapes,
  editMode = 'move',
  canUndo = false,
  canRedo = false,
  onSetEditMode,
  onUndoEdit,
  onRedoEdit,
  onCancelEdits,
}) => {
  const { shapeCount, pointCount } = getIpawsAreaMetrics(shapes);
  const selectedShape = shapes.find((shape) => shape.id === selectedShapeId);

  // Check both configured IPAWS limits before enabling a new shape. Different shapes use different
  // numbers of coordinate entries in the outgoing alert.
  const canAddShapeWithPointCost = (pointCost) =>
    shapeCount < IPAWS_AREA_LIMITS.maximumShapeCount &&
    pointCount + pointCost <= IPAWS_AREA_LIMITS.maximumPointCount;
  const canStartPolygon = canAddShapeWithPointCost(4); // 3 corners plus the repeated first point.
  const canPlaceSquare = canAddShapeWithPointCost(5); // 4 corners plus the repeated first point.
  const canPlaceCircle = canAddShapeWithPointCost(1); // CAP stores this as one circle entry.
  const selectedPolygonVertexCount =
    selectedShape?.type === IPAWS_AREA_TYPES.POLYGON ? (selectedShape.coordinates?.length ?? 0) : 0;
  // Reserve enough unused points to finish and close the polygon, not only its next visible point.
  const canAddPolygonVertex = selectedShape
    ? pointCount + getIpawsPolygonRequiredPointCapacity(selectedShape) <=
        IPAWS_AREA_LIMITS.maximumPointCount &&
      (!isNwemSelected ||
        (selectedShape.coordinates?.length ?? 0) < NWEM_MAXIMUM_DRAWN_POLYGON_VERTEX_COUNT)
    : canStartPolygon;
  // A polygon needs at least three different points before it can be kept.
  const isIncompleteActivePolygon =
    activeTool === IPAWS_AREA_TYPES.POLYGON && selectedPolygonVertexCount < 3;
  const canPlaceActiveShape =
    activeTool === IPAWS_AREA_TYPES.POLYGON
      ? canAddPolygonVertex
      : activeTool === IPAWS_AREA_TYPES.SQUARE
        ? canPlaceSquare
        : canPlaceCircle;
  const selectedShapeIndex = shapes.findIndex((shape) => shape.id === selectedShapeId);
  const selectedShapeLabel = selectedShape
    ? getIpawsAreaShapeLabel(selectedShape, selectedShapeIndex)
    : '';

  const getToolInstruction = () => {
    if (activeTool === IPAWS_AREA_TYPES.POLYGON) {
      return 'Click the map or add the map-center point for each polygon vertex.';
    }

    if (activeTool === IPAWS_AREA_TYPES.CIRCLE) {
      return 'Click the map or use its current center to place each circle.';
    }

    if (activeTool === IPAWS_AREA_TYPES.SQUARE) {
      return 'Click the map or use its current center to place each square.';
    }

    return 'Choose a shape, then place it on the map.';
  };

  const updateSelectedCenter = (coordinateName, rawValue) => {
    if (!selectedShape?.center) return;

    onUpdateSelectedShape({
      center: {
        ...selectedShape.center,
        // Keep an empty value while the user clears or replaces the number in the input.
        [coordinateName]: rawValue === '' ? '' : Number(rawValue),
      },
    });
  };

  return (
    <div className="alert-creator-area-tools d-flex flex-column gap-2">
      <ShapePlacementControls
        ariaLabel="Add an IPAWS alert area"
        activeTool={activeTool}
        canDrawPolygon={canStartPolygon}
        canPlaceSquare={canPlaceSquare}
        canPlaceCircle={canPlaceCircle}
        onStartTool={onStartTool}
      />

      <small className="alert-creator-area-tool-status" role="status" aria-live="polite">
        {getToolInstruction()}
      </small>

      {activeTool && (
        <div className="d-flex flex-column gap-1">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="map-tools-button"
            disabled={!canPlaceActiveShape}
            onClick={onPlaceAtMapCenter}
          >
            {activeTool === IPAWS_AREA_TYPES.POLYGON
              ? 'Add map-center vertex'
              : 'Place at map center'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="map-tools-button"
            onClick={isIncompleteActivePolygon ? onCancelTool : onFinishTool}
          >
            {activeTool === IPAWS_AREA_TYPES.POLYGON
              ? isIncompleteActivePolygon
                ? 'Cancel polygon'
                : 'Finish polygon'
              : 'Done placing'}
          </Button>
        </div>
      )}

      <div className="alert-creator-area-usage small" role="status" aria-live="polite">
        <strong>{shapeCount}</strong> / {IPAWS_AREA_LIMITS.maximumShapeCount} shapes ·{' '}
        <strong>{pointCount}</strong> / {IPAWS_AREA_LIMITS.maximumPointCount} polygon points
      </div>

      {shapes.length ? (
        <div>
          <div className="small fw-semibold mb-1">Placed shapes</div>
          <ol className="alert-creator-area-list list-unstyled d-flex flex-column gap-1 mb-0">
            {shapes.map((shape, index) => {
              const shapeLabel = getIpawsAreaShapeLabel(shape, index);
              const isSelected = shape.id === selectedShapeId;

              return (
                <li key={shape.id} className="d-flex align-items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className={`map-tools-button text-break mb-0 ${isSelected ? 'is-selected' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => onSelectShape(shape.id)}
                  >
                    {shapeLabel}
                  </Button>
                  {outsideShapeIds.includes(shape.id) ? (
                    <OutsideAreaBadge shapeLabel={shapeLabel} />
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      ) : null}

      {selectedShape && (
        <fieldset className="alert-creator-area-editor border rounded-2 p-2">
          <legend className="small fw-semibold text-break mb-1">Edit {selectedShapeLabel}</legend>

          <Form.Group className="mb-2" controlId="ipaws-area-shape-name">
            <Form.Label className="small mb-1">Shape name</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              value={selectedShape.name ?? selectedShapeLabel}
              aria-describedby="ipaws-area-shape-name-hint"
              onChange={(event) => onUpdateSelectedShape({ name: event.target.value })}
            />
            <Form.Text id="ipaws-area-shape-name-hint">
              Leave blank to use the default name.
            </Form.Text>
          </Form.Group>

          <GeometryToolsPanel
            activeMode={editMode}
            onSetMode={onSetEditMode}
            onUndo={onUndoEdit}
            onRedo={onRedoEdit}
            onCancel={onCancelEdits}
            canReshape={selectedShape.type === IPAWS_AREA_TYPES.POLYGON}
            canRotate={selectedShape.type === IPAWS_AREA_TYPES.POLYGON}
            canAddVertex={selectedShape.type === IPAWS_AREA_TYPES.POLYGON && canAddPolygonVertex}
            canEraseVertex={
              selectedShape.type === IPAWS_AREA_TYPES.POLYGON && selectedPolygonVertexCount > 3
            }
            canUndo={canUndo}
            canRedo={canRedo}
          />

          {selectedShape.type === IPAWS_AREA_TYPES.POLYGON ? (
            <small className="alert-creator-area-tool-status d-block mb-2">
              {editMode === 'addVertex'
                ? 'Click near the polygon border to add a vertex at the nearest edge point.'
                : editMode === 'eraseVertex'
                  ? 'Click a polygon vertex to remove it.'
                  : `${selectedPolygonVertexCount} drawn vertices`}
            </small>
          ) : null}

          {selectedShape.type === IPAWS_AREA_TYPES.POLYGON ? (
            <div className="d-flex flex-column gap-1">
              {activeTool !== IPAWS_AREA_TYPES.POLYGON && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="map-tools-button"
                  disabled={
                    pointCount + getIpawsPolygonRequiredPointCapacity(selectedShape) >
                      IPAWS_AREA_LIMITS.maximumPointCount ||
                    (isNwemSelected &&
                      (selectedShape.coordinates?.length ?? 0) >=
                        NWEM_MAXIMUM_DRAWN_POLYGON_VERTEX_COUNT)
                  }
                  onClick={onContinuePolygon}
                >
                  Continue adding vertices
                </Button>
              )}
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              <small className="alert-creator-area-tool-status">
                Drag the center marker to move. Drag{' '}
                {selectedShape.type === IPAWS_AREA_TYPES.CIRCLE
                  ? 'the perimeter handle'
                  : 'a corner handle'}{' '}
                to resize, or use the fields below for precise keyboard editing.
              </small>

              <div className="row g-2">
                <Form.Group className="col-12 col-sm-6" controlId="ipaws-area-center-latitude">
                  <Form.Label className="small mb-1">Latitude</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={-90}
                    max={90}
                    step="0.000001"
                    value={selectedShape.center?.lat ?? ''}
                    onChange={(event) => updateSelectedCenter('lat', event.target.value)}
                  />
                </Form.Group>
                <Form.Group className="col-12 col-sm-6" controlId="ipaws-area-center-longitude">
                  <Form.Label className="small mb-1">Longitude</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={-180}
                    max={180}
                    step="0.000001"
                    value={selectedShape.center?.lng ?? ''}
                    onChange={(event) => updateSelectedCenter('lng', event.target.value)}
                  />
                </Form.Group>
              </div>

              {selectedShape.type === IPAWS_AREA_TYPES.CIRCLE ? (
                <Form.Group controlId="ipaws-area-circle-radius">
                  <Form.Label className="small mb-1">Radius (kilometers)</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={MINIMUM_IPAWS_SHAPE_SIZE_KM}
                    step="0.1"
                    value={selectedShape.radiusKm ?? ''}
                    onChange={(event) =>
                      onUpdateSelectedShape({
                        radiusKm: event.target.value === '' ? '' : Number(event.target.value),
                      })
                    }
                  />
                </Form.Group>
              ) : (
                <Form.Group controlId="ipaws-area-square-side-length">
                  <Form.Label className="small mb-1">Side length (kilometers)</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={MINIMUM_IPAWS_SHAPE_SIZE_KM}
                    step="0.1"
                    value={selectedShape.sideLengthKm ?? ''}
                    onChange={(event) =>
                      onUpdateSelectedShape({
                        sideLengthKm: event.target.value === '' ? '' : Number(event.target.value),
                      })
                    }
                  />
                </Form.Group>
              )}
            </div>
          )}

          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="map-tools-button alert-creator-area-delete text-break mt-2"
            onClick={onDeleteSelectedShape}
          >
            Delete {selectedShapeLabel}
          </Button>
        </fieldset>
      )}

      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="map-tools-button alert-creator-area-clear"
        disabled={!shapes.length}
        onClick={onClearShapes}
      >
        Clear all shapes
      </Button>

      <small className="map-tools-hint">
        FEMA guidance: Keep shapes simple, within authorized boundaries, and large enough for
        reliable WEA targeting. Shapes should generally cover at least two city blocks.
      </small>
    </div>
  );
};

export default IpawsAreaToolsPanel;
